import {useMemo, useState} from 'react';
import {
  Users,
  Building2,
  TrendingUp,
  Network,
  List,
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {trpc} from '@common-web/lib/trpc';
import {ACCOUNTS_SUB_STAGES, CANONICAL_SUB_STAGE_PHASE} from '@common/accountsView';
import {HeroBand} from '@client/components/broker-ui/hero-band';
import {PillToggle} from '@client/components/broker-ui/pill-toggle';
import {StatCard} from '@client/components/broker-ui/stat-card';
import {BrokerNodeCard} from '@client/components/broker-ui/broker-node-card';
import {InitialsAvatar} from '@client/components/broker-ui/initials-avatar';
import {Pill, RolePill} from '@client/components/broker-ui/pills';

// Prospect stage = the LEAD phase minus the distinct SWOT Call stage. Kept in
// sync with the Accounts "Prospects" count (broker.getTeam), which excludes
// SWOT Call the same way. Mirrors the legacy BrokerTeam page exactly.
const PROSPECT_SUB_STAGES = ACCOUNTS_SUB_STAGES.filter(
  (s) => CANONICAL_SUB_STAGE_PHASE[s] === 'LEAD' && s !== 'SWOT_CALL',
);

type ViewMode = 'tree' | 'list';
type SortKey = 'name' | 'prospects' | 'onboarding' | 'deals' | 'groups' | 'commission';
type SortDirection = 'asc' | 'desc';
type SortState = {key: SortKey; direction: SortDirection} | null;

const DEFAULT_SORT: SortState = {key: 'name', direction: 'asc'};

interface TeamBroker {
  id: string;
  name: string;
  email: string | null;
  parentAgentId: string | null;
  parentName: string | null;
  depth: number;
  isSelf: boolean;
  isActive: boolean;
  isSuspended: boolean;
  prospectCount: number;
  onboardingCount: number;
  dealCount: number;
  activeGroupCount: number;
  memberCount: number;
  estimatedMonthlyCommission: number;
}

interface TeamNode extends TeamBroker {
  children: TeamNode[];
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

// Deep link into the Accounts page pre-filtered on the downline broker (and an
// optional lifecycle phase), opened in a new tab so the team view stays put.
// Same helpers as the legacy page, just hoisted to module scope so both the
// tree cards and the list rows can share them.
const openInNewTab = (path: string) => window.open(path, '_blank', 'noopener,noreferrer');
const openAccounts = (brokerId: string, phase?: 'ONBOARDING' | 'ACTIVE') =>
  openInNewTab(`/accounts?broker=${brokerId}${phase ? `&phase=${phase}` : ''}`);
const goToProspects = (brokerId: string) =>
  openInNewTab(`/accounts?broker=${brokerId}&subStage=${PROSPECT_SUB_STAGES.join(',')}`);
const goToOnboarding = (brokerId: string) => openAccounts(brokerId, 'ONBOARDING');
const goToGroups = (brokerId: string) => openAccounts(brokerId, 'ACTIVE');
const goToPipeline = (brokerId: string) => openAccounts(brokerId);

/** Small asc/desc/unsorted indicator matching the redesign's muted-ink palette. */
function SortIndicator({direction}: {direction: SortDirection | null}) {
  if (direction === 'asc') return <ArrowUp size={12} className='text-[var(--nbui-ink-500)]' />;
  if (direction === 'desc') return <ArrowDown size={12} className='text-[var(--nbui-ink-500)]' />;
  return <ArrowUpDown size={12} className='text-[var(--nbui-ink-400)] opacity-50' />;
}

/** Sort toggle cycle mirroring the legacy page's shared table hook: off → asc → desc → off. */
function nextSort(current: SortState, clickedKey: SortKey): SortState {
  if (!current || current.key !== clickedKey) return {key: clickedKey, direction: 'asc'};
  if (current.direction === 'asc') return {key: clickedKey, direction: 'desc'};
  return null;
}

function rolePillFor(node: {isSelf: boolean; children: TeamNode[]}) {
  if (node.isSelf) return <Pill color='var(--nbui-accent-blue)'>You</Pill>;
  return node.children.length > 0 ? <RolePill kind='upline' /> : <RolePill kind='individual' />;
}

function nodeCardFooter(brokerId: string) {
  return (
    <>
      <button
        type='button'
        onClick={() => goToPipeline(brokerId)}
        className='text-[12px] font-medium text-[var(--nbui-accent-blue)] hover:underline'
      >
        View Pipeline
      </button>
      <button
        type='button'
        onClick={() => goToGroups(brokerId)}
        className='ml-auto text-[12px] font-medium text-[var(--nbui-ink-500)] hover:underline'
      >
        View Groups
      </button>
    </>
  );
}

function TreeNodeView({node, depth}: {node: TeamNode; depth: number}) {
  const [collapsed, setCollapsed] = useState(false);
  const hasChildren = node.children.length > 0;

  return (
    <div className='flex flex-col items-center'>
      <div className='relative'>
        {depth > 0 && (
          <div className='absolute left-1/2 -top-6 w-px h-6 bg-[var(--nbui-border-mid)]' />
        )}
        <BrokerNodeCard
          name={node.name}
          email={node.email ?? ''}
          rolePill={rolePillFor(node)}
          stats={[
            {label: 'Groups', value: node.activeGroupCount},
            {label: 'Deals', value: node.dealCount},
            {label: 'Prospects', value: node.prospectCount},
          ]}
          footer={nodeCardFooter(node.id)}
        />
        {hasChildren && (
          <button
            type='button'
            onClick={() => setCollapsed((c) => !c)}
            className='absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-white border border-[var(--nbui-border-soft)] flex items-center justify-center text-[var(--nbui-ink-500)] hover:bg-[#F8FAFC] cursor-pointer z-10'
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
      </div>

      {hasChildren && !collapsed && (
        <div className='mt-8 flex gap-8 relative'>
          <div
            className='absolute top-0 left-1/2 -translate-x-1/2 h-px bg-[var(--nbui-border-mid)]'
            style={{width: `${Math.max(0, (node.children.length - 1) * 312)}px`}}
          />
          {node.children.map((child) => (
            <TreeNodeView key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

const LIST_COLUMNS: {key: string; label: string; sortKey?: SortKey}[] = [
  {key: 'broker', label: 'Broker', sortKey: 'name'},
  {key: 'depth', label: 'Depth'},
  {key: 'upline', label: 'Upline'},
  {key: 'prospects', label: 'Prospects', sortKey: 'prospects'},
  {key: 'onboarding', label: 'Onboarding', sortKey: 'onboarding'},
  {key: 'deals', label: 'Deals', sortKey: 'deals'},
  {key: 'groups', label: 'Active Groups', sortKey: 'groups'},
  {key: 'commission', label: 'Est. Monthly', sortKey: 'commission'},
];

/**
 * Redesigned broker team page for the `newBrokerUi` flag: hero band + stat
 * row + a tree/list toggle mirroring the synrgyinfinity demo. Reuses the
 * exact `broker.getTeam` query and deep-link helpers as the legacy page.
 */
export const BrokerTeamRedesign = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const toggleSort = (key: SortKey) => setSort((current) => nextSort(current, key));

  const {data, isLoading, error} = trpc.broker.getTeam.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  const tree = (data?.tree ?? []) as TeamNode[];
  const self = (data?.self ?? null) as TeamBroker | null;
  const downlineBrokers = useMemo(
    () => ((data?.brokers ?? []) as TeamBroker[]).filter((b) => !b.isSelf),
    [data?.brokers],
  );

  // Same client-side-only sort pattern as the legacy page and the demo: the
  // team query loads the whole downline at once, so there's no server-side
  // sort/pagination to preserve. Direction mirrors the legacy page's
  // table.sort.direction toggle (see nextSort above).
  const sortedBrokers = useMemo(() => {
    const arr = [...downlineBrokers];
    const dir = sort?.direction === 'desc' ? -1 : 1;
    switch (sort?.key) {
      case 'prospects':
        return arr.sort((a, b) => (a.prospectCount - b.prospectCount) * dir);
      case 'onboarding':
        return arr.sort((a, b) => (a.onboardingCount - b.onboardingCount) * dir);
      case 'deals':
        return arr.sort((a, b) => (a.dealCount - b.dealCount) * dir);
      case 'groups':
        return arr.sort((a, b) => (a.activeGroupCount - b.activeGroupCount) * dir);
      case 'commission':
        return arr.sort(
          (a, b) => (a.estimatedMonthlyCommission - b.estimatedMonthlyCommission) * dir,
        );
      case 'name':
        return arr.sort((a, b) => a.name.localeCompare(b.name) * dir);
      default:
        return arr;
    }
  }, [downlineBrokers, sort]);

  if (error) {
    return (
      <div className='px-8 py-6'>
        <Alert variant='destructive'>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='px-8 py-6'>
      <HeroBand title='My Team' subtitle='Read-only view of your downline brokers and their books.'>
        <PillToggle
          options={[
            {value: 'tree', label: 'Tree', icon: <Network size={14} />},
            {value: 'list', label: 'List', icon: <List size={14} />},
          ]}
          value={viewMode}
          onChange={(v) => setViewMode(v as ViewMode)}
        />
      </HeroBand>

      {isLoading ? (
        <div className='flex items-center justify-center py-24'>
          <Loader2 className='h-8 w-8 animate-spin text-[var(--nbui-accent-blue)]' />
        </div>
      ) : (
        <>
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-5 mb-5'>
            <StatCard
              label='Direct Downlines'
              value={data?.stats.directDownlines ?? 0}
              icon={<Users size={18} />}
              iconColor='var(--nbui-accent-blue)'
            />
            <StatCard
              label='Total Team Size'
              value={data?.stats.totalTeamSize ?? 0}
              icon={<Users size={18} />}
              iconColor='var(--nbui-accent-teal)'
            />
            <StatCard
              label='Team Active Groups'
              value={data?.stats.teamActiveGroups ?? 0}
              icon={<Building2 size={18} />}
              iconColor='var(--nbui-accent-green)'
            />
            <StatCard
              label='Est. Monthly Commission'
              value={formatCurrency(data?.stats.teamEstimatedMonthlyCommission ?? 0)}
              icon={<TrendingUp size={18} />}
              iconColor='var(--nbui-accent-amber)'
            />
          </div>

          {downlineBrokers.length === 0 ? (
            <div className='bg-white border border-[var(--nbui-border-soft)] rounded-[14px] flex flex-col items-center justify-center gap-2 py-16 text-center'>
              <Users className='h-10 w-10 text-[var(--nbui-ink-400)]' />
              <p className='text-lg font-semibold text-[var(--nbui-ink-900)]'>
                No downline brokers yet
              </p>
              <p className='max-w-md text-sm text-[var(--nbui-ink-500)]'>
                When brokers are placed under you, you&apos;ll see their pipeline and live groups
                here.
              </p>
            </div>
          ) : viewMode === 'tree' ? (
            <div className='overflow-x-auto pb-4'>
              <div className='flex flex-col items-center min-w-max pt-4'>
                {self && (
                  <div className='flex flex-col items-center'>
                    <BrokerNodeCard
                      name={self.name}
                      email={self.email ?? ''}
                      highlighted
                      rolePill={<Pill color='var(--nbui-accent-blue)'>You</Pill>}
                      stats={[
                        {label: 'Groups', value: self.activeGroupCount},
                        {label: 'Deals', value: self.dealCount},
                        {label: 'Prospects', value: self.prospectCount},
                      ]}
                      footer={nodeCardFooter(self.id)}
                    />
                    {tree.length > 0 && (
                      <div className='w-px h-6 bg-[var(--nbui-border-mid)] mt-1' />
                    )}
                  </div>
                )}
                {tree.length > 0 && (
                  <div className='flex gap-8 relative'>
                    <div
                      className='absolute top-0 left-1/2 -translate-x-1/2 h-px bg-[var(--nbui-border-mid)]'
                      style={{width: `${Math.max(0, (tree.length - 1) * 312)}px`}}
                    />
                    {tree.map((child) => (
                      <TreeNodeView key={child.id} node={child} depth={1} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className='bg-white border border-[var(--nbui-border-soft)] rounded-[14px] overflow-hidden'>
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse min-w-[900px]'>
                  <thead>
                    <tr className='border-b border-[var(--nbui-border-soft)]'>
                      {LIST_COLUMNS.map((col) => {
                        const sortKey = col.sortKey;
                        const sortState = sortKey && sort?.key === sortKey ? sort.direction : null;
                        return (
                          <th
                            key={col.key}
                            aria-sort={
                              sortKey
                                ? sortState === 'asc'
                                  ? 'ascending'
                                  : sortState === 'desc'
                                    ? 'descending'
                                    : 'none'
                                : undefined
                            }
                            className='text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--nbui-ink-500)] whitespace-nowrap'
                          >
                            {sortKey ? (
                              <button
                                type='button'
                                onClick={() => toggleSort(sortKey)}
                                className='inline-flex items-center gap-1 cursor-pointer hover:text-[var(--nbui-ink-900)]'
                              >
                                {col.label}
                                <SortIndicator direction={sortState} />
                              </button>
                            ) : (
                              <span className='inline-flex items-center gap-1'>{col.label}</span>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBrokers.map((b) => (
                      <tr
                        key={b.id}
                        className='border-b border-[#F1F5F9] last:border-0 hover:bg-[#F9FAFB] transition'
                      >
                        <td className='px-6 py-[18px]'>
                          <div className='flex items-center gap-3'>
                            <InitialsAvatar name={b.name} size={32} />
                            <div className='min-w-0'>
                              <p className='text-[14px] font-semibold text-[var(--nbui-ink-900)] truncate'>
                                {b.name}
                              </p>
                              {b.email && (
                                <p className='text-[12px] text-[var(--nbui-ink-500)] truncate'>
                                  {b.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className='px-6 py-[18px]'>
                          <span className='inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F1F5F9] text-[12px] font-semibold text-[var(--nbui-ink-500)]'>
                            L{b.depth}
                          </span>
                        </td>
                        <td className='px-6 py-[18px] text-[14px] text-[var(--nbui-ink-500)]'>
                          {b.parentName || '\u2014'}
                        </td>
                        <td className='px-6 py-[18px] text-[14px] tabular-nums'>
                          <button
                            type='button'
                            onClick={() => goToProspects(b.id)}
                            className='font-medium text-[var(--nbui-accent-blue)] hover:underline'
                          >
                            {b.prospectCount}
                          </button>
                        </td>
                        <td className='px-6 py-[18px] text-[14px] tabular-nums'>
                          <button
                            type='button'
                            onClick={() => goToOnboarding(b.id)}
                            className='font-medium text-[var(--nbui-accent-blue)] hover:underline'
                          >
                            {b.onboardingCount}
                          </button>
                        </td>
                        <td className='px-6 py-[18px] text-[14px] text-[var(--nbui-ink-900)] tabular-nums'>
                          {b.dealCount}
                        </td>
                        <td className='px-6 py-[18px] text-[14px] tabular-nums'>
                          <button
                            type='button'
                            onClick={() => goToGroups(b.id)}
                            className='font-medium text-[var(--nbui-accent-blue)] hover:underline'
                          >
                            {b.activeGroupCount}
                          </button>
                        </td>
                        <td className='px-6 py-[18px] text-[14px] text-[var(--nbui-ink-900)] tabular-nums'>
                          {formatCurrency(b.estimatedMonthlyCommission)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
